import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import LocalStorage from "../../helper/LocalStorage";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import { useProjectContext } from "../../context/useProjectContext";
import { useLicensingContext } from "../../context/useLicensingContext";
import { showErrorNotification, showGenericError } from "../../helper/Notifications";

const SSOAuthSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { createProject } = useProjectContext();
  const { subscribeToOffer, offers } = useLicensingContext();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const ssoAccessToken = searchParams.get("token");
    const ssoRefreshToken = searchParams.get("refresh_token");
    const error = searchParams.get("error");

    if (ssoAccessToken && ssoAccessToken !== "") {
      LocalStorage.setSSOFlag(true);
      LocalStorage.setAuthenthicationToken(ssoAccessToken);
      LocalStorage.setRefreshToken(ssoRefreshToken);

      // Check if this is an enterprise registration flow
      const enterpriseContext = LocalStorage.getEnterpriseRegistrationContext();

      if (enterpriseContext) {
        // Handle enterprise registration flow
        handleEnterpriseRegistration(enterpriseContext);
      } else {
        // Navigate to the default landing page
        navigate("/dashboard");
      }
    } else {
      if (error === "provider_mismatch") {
        showErrorNotification("This email is already linked to a different sign-in method. Please use your original login method.");
      }
      navigate("/login");
    }
  }, [searchParams, navigate]);

  const handleEnterpriseRegistration = (context) => {
    const { plan, projectName, currency } = context;

    // First, create the project
    createProject(
      projectName || "My Project",
      [], // No members to invite initially
      (response) => {
        const project = response.data.project;

        // Fetch offers to get the correct offer name for the plan
        offers(
          (offersResponse) => {
            const offersData = offersResponse.data?.response || [];

            // Map plan parameter to offer name (assuming plan comes as a simple identifier)
            // You may need to adjust this mapping based on your actual plan values
            const offerName = findOfferNameByPlan(offersData, plan);

            if (!offerName) {
              showErrorNotification("Selected plan not found");
              LocalStorage.removeEnterpriseRegistrationContext();
              navigate("/dashboard");
              return;
            }

            // Subscribe to the offer
            subscribeToOffer(
              project.id,
              offerName,
              currency || "eur",
              (subscriptionResponse) => {
                // Clean up the enterprise context
                LocalStorage.removeEnterpriseRegistrationContext();

                if (subscriptionResponse.data.url) {
                  // Redirect to Stripe checkout
                  window.location = subscriptionResponse.data.url;
                } else {
                  // Subscription created without Stripe redirect
                  navigate("/dashboard");
                }
              },
              (error) => {
                console.error("Subscription error:", error);
                showGenericError();
                LocalStorage.removeEnterpriseRegistrationContext();
                navigate("/dashboard");
              }
            );
          },
          (error) => {
            console.error("Offers fetch error:", error);
            showGenericError();
            LocalStorage.removeEnterpriseRegistrationContext();
            navigate("/dashboard");
          }
        );
      },
      (error) => {
        console.error("Project creation error:", error);
        showGenericError();
        LocalStorage.removeEnterpriseRegistrationContext();
        navigate("/dashboard");
      }
    );
  };

  // Helper function to find the correct offer name based on plan
  const findOfferNameByPlan = (offersData, plan) => {
    // This mapping should match your actual plan names and offer names
    // Adjust based on your backend's offer structure
    if (!plan) {
      // Default to monthly B2B Pro if no plan specified
      const monthlyOffer = offersData.find(
        (offer) => offer.name?.includes("B2B Pro") && offer.interval === "month"
      );
      return monthlyOffer?.name;
    }

    // Try to find an offer that matches the plan parameter
    const matchingOffer = offersData.find(
      (offer) => offer.name?.toLowerCase().includes(plan.toLowerCase())
    );

    return matchingOffer?.name;
  };

  return (
    <LoaderComponent loading={true}>
      <Container />
    </LoaderComponent>
  );
};

export default SSOAuthSuccessPage;

const Container = styled.div`
  display: flex;
  max-height: 100vh;
  height: 100%;
  min-height: 600px;
  width: 100%;
  align-items: center;
  overflow: auto;
  position: relative;
`;
